"use client";
import React, { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { FaPlay, FaTimes } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import Lesson from "./LessonComponent";

const lessonTypes = [
  {
    name: "Piano",
    description:
      "From classical to contemporary, unlock the beauty of the piano. Suitable for beginners and intermediate players looking to refine their skills.",
    videoId: "LN_BdJxsAuM",
  },
  {
    name: "Trombone",
    description:
      "Master the slide and produce rich, warm tones. Perfect for aspiring jazz musicians or classical enthusiasts.",
    videoId: "n6x4agNFUgM",
  },
  {
    name: "Composition",
    description:
      "Turn your musical ideas into fully-fledged compositions. Learn songwriting, arranging, and music theory.",
    videoId: "C1KQdSqU9Jc",
  },
  {
    name: "Beginner Bagpipe",
    description:
      "Start your journey into Scottish musical tradition. Learn the basics of this iconic instrument.",
    videoId: "kM4o0hfCebc",
  },
  {
    name: "Cello",
    description:
      "Discover the soulful, resonant tones of the cello. From Bach to contemporary pieces, develop your skills on this magnificent instrument.",
    videoId: "0ZjB6hzdZos",
  },
];

const LessonsPage: React.FC = () => {
  const [showVideo, setShowVideo] = useState(false);
  const [activeLesson, setActiveLesson] = useState<number | null>(null);

  const getYouTubeEmbedUrl = (videoId: string) => {
    return `https://www.youtube.com/embed/${videoId}`;
  };

  return (
    <motion.div
      className="min-h-screen bg-transparent text-white font-goodfont"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
    >
      <div className="container mx-auto px-4 py-8 sm:py-16">
        <motion.h1
          className="text-4xl sm:text-5xl mb-8 sm:mb-12 text-center uppercase tracking-widest"
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          Music Lessons
        </motion.h1>

        {/* Pricing Section */}
        <motion.div
          className="mb-8 sm:mb-16"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <h2 className="text-2xl sm:text-3xl mb-6 sm:mb-8 text-center uppercase tracking-wider">
            Pricing
          </h2>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-8">
            <motion.div
              className="border border-white p-4 sm:p-6 text-center w-full sm:w-48"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-lg sm:text-xl text-gray-400 mb-2">
                30 minutes
              </p>
              <p className="text-2xl sm:text-3xl font-bold">$20</p>
            </motion.div>
            <motion.div
              className="border border-white p-4 sm:p-6 text-center w-full sm:w-48"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              <p className="text-lg sm:text-xl text-gray-400 mb-2">1 hour</p>
              <p className="text-2xl sm:text-3xl font-bold">$40</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Lesson Types Section */}
        <motion.div
          className="mb-8 sm:mb-16"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h2 className="text-2xl sm:text-3xl mb-6 sm:mb-8 text-center uppercase tracking-wider">
            Lesson Types
          </h2>
          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8"
            variants={{
              hidden: { opacity: 0 },
              show: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.1,
                },
              },
            }}
            initial="hidden"
            animate="show"
          >
            {lessonTypes.map((lesson, index) => (
              <Lesson
                key={index}
                name={lesson.name}
                description={lesson.description}
                videoId={lesson.videoId}
                onWatchClick={() => {
                  setActiveLesson(index);
                  setShowVideo(true);
                }}
              />
            ))}
          </motion.div>
        </motion.div>

        <AnimatePresence>
          {showVideo &&
            activeLesson !== null &&
            lessonTypes[activeLesson].videoId && (
              <motion.div
                className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div
                  className="bg-white rounded-lg w-full max-w-4xl"
                  initial={{ scale: 0.9 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.9 }}
                >
                  <div className="relative pt-[56.25%]">
                    <iframe
                      className="absolute top-0 left-0 w-full h-full"
                      src={getYouTubeEmbedUrl(
                        lessonTypes[activeLesson].videoId
                      )}
                      title={`${lessonTypes[activeLesson].name} Lesson Demo`}
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                  <div className="p-4">
                    <motion.button
                      onClick={() => setShowVideo(false)}
                      className="w-full bg-black text-white py-2 px-4 rounded hover:bg-gray-800 transition duration-300"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      <FaTimes className="inline mr-2" /> Close
                    </motion.button>
                  </div>
                </motion.div>
              </motion.div>
            )}
        </AnimatePresence>

        {/* Call to Action */}
        <motion.div
          className="text-center border border-white p-4 sm:p-8"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <h2 className="text-2xl sm:text-3xl mb-4 uppercase tracking-wider">
            Ready to Start?
          </h2>
          <p className="text-base sm:text-xl text-gray-400 mb-6">
            Contact me to schedule your lesson or for any questions.
          </p>
          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Link href="/contact" passHref>
              <span className="inline-block bg-black text-white py-2 sm:py-3 px-6 sm:px-8 border border-white hover:bg-white hover:text-black transition duration-300 cursor-pointer">
                Contact Me
              </span>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default LessonsPage;
